import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Video, LinkIcon, Upload, Trash2, Plus, Loader2, BookOpenCheck, ExternalLink } from "lucide-react";

const KIND_ICON = { pdf: FileText, image: ImageIcon, video: Video, link: LinkIcon, document: FileText };
const SUGGESTED_CATS = ["Allergy and Flu", "Hypertension", "Diabetes", "Appliances", "Antibiotics", "Maternal Health", "General"];

const CATEGORY_COVERS = [
  [/diabet/i, "diabetes"],
  [/hyperten|blood pressure/i, "hypertension"],
  [/allerg|flu|cold/i, "allergy_flu"],
  [/applianc|device/i, "appliances"],
  [/antibiot|infection/i, "antibiotics"],
  [/maternal|pregnan|mother|antenatal/i, "maternal"],
];
function categoryCover(category) {
  const c = category || "";
  for (const [re, slug] of CATEGORY_COVERS) if (re.test(c)) return `/covers/${slug}.jpg`;
  return "/covers/general.jpg";
}

export default function KnowledgeBank() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || (user?.permissions || []).includes("manage_resources");
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [viewer, setViewer] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", category: "General", kind: "pdf", description: "", url: "", file: null, cover: null });

  const load = useCallback(async () => {
    const url = cat && cat !== "all" ? `/knowledge?category=${encodeURIComponent(cat)}` : "/knowledge";
    try {
      const { data } = await api.get(url);
      setItems(data);
    } catch (error) {
      console.error("Failed to load knowledge items:", error);
      toast.error("Unable to load Knowledge Bank items.");
    }
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const s = new Set(items.map((i) => i.category));
    return ["all", ...Array.from(s)];
  }, [items]);

  // The httpOnly auth cookie is sent automatically with same-origin file requests.
  const fileUrl = (i) => `${API}/knowledge/${i.id}/file`;
  const coverUrl = (i) => (i.cover_path ? `${API}/knowledge/${i.id}/cover` : categoryCover(i.category));

  const openItem = (i) => {
    if (i.kind === "link" || (i.kind === "video" && i.url)) { window.open(i.url, "_blank"); return; }
    setViewer(i);
  };

  const submit = async () => {
    if (!form.title || !form.category) { toast.error("Add a title and category"); return; }
    if ((form.kind === "link") && !form.url) { toast.error("Add a URL"); return; }
    if (["pdf", "image", "document"].includes(form.kind) && !form.file && !form.url) { toast.error("Choose a file"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("kind", form.kind);
    fd.append("description", form.description);
    fd.append("url", form.url);
    if (form.file) fd.append("file", form.file);
    if (form.cover) fd.append("cover", form.cover);
    try {
      await api.post("/knowledge", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to Knowledge Bank");
      setOpen(false);
      setForm({ title: "", category: "General", kind: "pdf", description: "", url: "", file: null, cover: null });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setSaving(false); }
  };

  const del = async (i) => { if (!window.confirm(`Delete "${i.title}"?`)) return; await api.delete(`/knowledge/${i.id}`); load(); };

  return (
    <Layout nav={getNavFor(user)} showTestButton={user?.role !== "dispenser"}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Knowledge Bank</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manuals, guides, images and videos to read at your convenience. Filter by category to find what you need.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="knowledge-add-button"><Plus className="mr-1.5 h-4 w-4" />Add item</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto canda-scroll">
              <DialogHeader><DialogTitle className="font-heading">Add to Knowledge Bank</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="knowledge-title-input" /></div>
                <div className="space-y-2"><Label>Category</Label>
                  <Input list="cat-suggestions" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="knowledge-category-input" placeholder="e.g. Diabetes" />
                  <datalist id="cat-suggestions">{SUGGESTED_CATS.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="knowledge-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF / Manual</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video (file or link)</SelectItem>
                      <SelectItem value="link">Web link</SelectItem>
                      <SelectItem value="document">Other document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="knowledge-desc-input" /></div>
                {(form.kind === "link" || form.kind === "video") && (
                  <div className="space-y-2"><Label>URL {form.kind === "video" ? "(YouTube/Vimeo or leave blank to upload file)" : ""}</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="knowledge-url-input" /></div>
                )}
                {form.kind !== "link" && (
                  <div className="space-y-2"><Label>File</Label><Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="knowledge-file-input" /></div>
                )}
                <div className="space-y-2">
                  <Label>Cover image <span className="text-muted-foreground">(optional)</span></Label>
                  <Input type="file" accept="image/*" onChange={(e) => setForm({ ...form, cover: e.target.files?.[0] || null })} data-testid="knowledge-cover-input" />
                  <p className="text-xs text-muted-foreground">Leave blank to use a tasteful cover based on the category.</p>
                </div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={saving} data-testid="knowledge-submit">{saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Saving…</> : "Add item"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Category filter */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="knowledge-categories">
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`knowledge-cat-${c}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300" : "border-border bg-card text-muted-foreground hover:border-sky-300"}`}>
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No items in this category yet.</p>}
        {items.map((i) => {
          const Icon = KIND_ICON[i.kind] || FileText;
          return (
            <Card key={i.id} className="group flex flex-col overflow-hidden p-0" data-testid={`knowledge-item-${i.id}`}>
              <button onClick={() => openItem(i)} className="flex flex-1 flex-col text-left">
                <div className="relative h-36 w-full overflow-hidden bg-sky-50 dark:bg-sky-950">
                  <img src={coverUrl(i)} alt={i.title} loading="lazy"
                    onError={(e) => { if (!e.currentTarget.src.endsWith("/covers/general.jpg")) e.currentTarget.src = "/covers/general.jpg"; }}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <Badge variant="outline" className="absolute right-2 top-2 bg-background/80 text-[10px] capitalize backdrop-blur">{i.kind}</Badge>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600 dark:bg-sky-950"><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <div className="font-heading text-base font-bold leading-tight text-foreground">{i.title}</div>
                      <Badge className="mt-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]">{i.category}</Badge>
                    </div>
                  </div>
                  <div className="mt-2.5 line-clamp-2 text-xs text-muted-foreground">{i.description || "Tap to open and read."}</div>
                </div>
              </button>
              <div className="flex items-center justify-between border-t border-border px-5 py-2.5">
                <Button variant="ghost" size="sm" onClick={() => openItem(i)} data-testid={`knowledge-open-${i.id}`}>
                  <BookOpenCheck className="mr-1.5 h-4 w-4" />Open{(i.kind === "link" || (i.kind === "video" && i.url)) && <ExternalLink className="ml-1 h-3 w-3" />}
                </Button>
                {canManage && <Button variant="ghost" size="icon" onClick={() => del(i)} data-testid={`knowledge-delete-${i.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="h-[90vh] w-[95vw] max-w-5xl overflow-hidden p-0" data-testid="knowledge-viewer">
          <DialogTitle className="flex items-center gap-3 border-b border-border px-4 py-3">
            {viewer && <img src={coverUrl(viewer)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-10 w-16 shrink-0 rounded object-cover" />}
            <div className="min-w-0">
              <div className="truncate font-heading text-sm font-bold text-foreground">{viewer?.title}</div>
              <div className="text-xs text-muted-foreground">{viewer?.category}</div>
            </div>
          </DialogTitle>
          <div className="h-full w-full overflow-auto bg-muted/30 p-4 canda-scroll">
            {viewer?.kind === "image" && <img src={fileUrl(viewer)} alt={viewer.title} className="mx-auto max-h-full rounded-lg" />}
            {viewer?.kind === "video" && !viewer?.url && <video src={fileUrl(viewer)} controls className="mx-auto max-h-full w-full rounded-lg" />}
            {(viewer?.kind === "pdf" || viewer?.kind === "document") && <iframe title={viewer.title} src={fileUrl(viewer)} className="h-full w-full rounded-lg" />}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
